import type { TFunction } from 'i18next'
import type { SaasApiError } from '../../../api'

/** Canonical error_code values from backend / worker (do not invent new API codes here). */
export const MOTION_RECONSTRUCTION_ERROR_CODES = [
  'STALE_LIFECYCLE',
  'RECONSTRUCTION_STATE_DESYNC',
  'MOTION_JOB_FAILED',
  'MOTION_JOB_CANCELLED',
  'RECONSTRUCTION_FAILED',
  'MOTION_EXTRACTION_FAILED',
  'POSE_EXTRACTION_FAILED',
  'ENGINE_LOAD_FAILED',
  'MOTION_ASSET_NOT_FOUND',
  'VIDEO_ASSET_NOT_FOUND',
  'OUTPUT_UPLOAD_FAILED',
  'OUTPUT_SERIALIZATION_FAILED',
  'NO_PERSON_DETECTED',
  'INPUT_VIDEO_REQUIRED',
  'WORKER_UNAVAILABLE',
  'PROCESSING_FAILED',
  'RESULT_UNAVAILABLE',
  'motion_reconstruction_already_active',
] as const

const CODE_KEY_ALIASES: Record<string, string> = {
  RECONSTRUCTION_STATE_DESYNC: 'STALE_LIFECYCLE',
}

const CJK_RE = /[\u4e00-\u9fff]/

function motionErrorKey(code: string): string {
  const normalized = CODE_KEY_ALIASES[code] || code
  return `motionReconstruction.errors.codes.${normalized}`
}

function looksLikeInternalTechnicalMessage(message: string): boolean {
  const trimmed = message.trim()
  if (!trimmed) return false
  if (CJK_RE.test(trimmed)) return true
  if (/traceback|exception|error:\s/i.test(trimmed)) return true
  if (/^[\w.]+Error:/.test(trimmed)) return true
  return false
}

/** Dev-only — never shown in tenant UI. */
export function logMotionReconstructionErrorDev(
  code: string | null | undefined,
  technicalDetail: string | null | undefined,
  requestId?: string | null,
): void {
  if (!import.meta.env.DEV) return
  if (!code && !technicalDetail) return
  // eslint-disable-next-line no-console
  console.warn('[motion-reconstruction]', { error_code: code, technical_detail: technicalDetail, request_id: requestId })
}

/**
 * Map backend/worker error_code → tenant-facing English copy.
 * Never surfaces raw backend error_message strings.
 */
export function localizeMotionReconstructionError(
  err:
    | Pick<SaasApiError, 'code' | 'message' | 'status'>
    | { code?: string | null; message?: string | null; status?: number | null },
  t: TFunction<'canvas'>,
): string {
  const code = (err.code || '').trim()

  // VistoeraTASK-00263-20260928-1824-005 (P0 AI Credit Gate audit) —
  // ``credit_gate_service`` raises ``InsufficientCreditsError`` (code
  // "insufficient_credits", HTTP 402) with a Chinese default message
  // that ``looksLikeInternalTechnicalMessage`` below would otherwise
  // suppress as "looks like an internal technical message" (any CJK
  // text). Handle this BEFORE that generic check so tenants always see
  // an explicit "insufficient credits" message on this Canvas node too
  // (task spec §七), matching the same pattern already used by
  // voiceTemplateError.ts / generateAudioError.ts.
  if (code === 'member_credit_limit_exceeded') {
    return t('generation.errors.memberCreditLimitExceeded')
  }
  if (code === 'insufficient_credits' || err.status === 402) {
    return t('motionReconstruction.errors.pointsInsufficient')
  }

  if (code) {
    const key = motionErrorKey(code)
    const localized = t(key, { defaultValue: '' })
    if (localized) return localized
  }

  const message = (err.message || '').trim()
  if (message && !looksLikeInternalTechnicalMessage(message)) {
    // Only allow through if it is already safe English product copy (legacy persisted fields).
    return message
  }

  return t('motionReconstruction.errors.fallback')
}

/** Resolve FAILED UI from persisted canvas fields (errorCode preferred over legacy errorMessage). */
export function resolveMotionReconstructionFailedDisplay(
  fields: { errorCode?: string | null; errorMessage?: string | null },
  t: TFunction<'canvas'>,
): string {
  if (fields.errorCode) {
    return localizeMotionReconstructionError({ code: fields.errorCode }, t)
  }
  const legacy = (fields.errorMessage || '').trim()
  if (legacy && !looksLikeInternalTechnicalMessage(legacy)) {
    return legacy
  }
  return t('motionReconstruction.errors.fallback')
}
